import React from "react";
import { Control, useWatch } from "react-hook-form";

import TooltipComponent from "components/ui/tooltip/Tooltip";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { Checkbox, DateFilter, DateRange } from "../../form_components/Inputs";
import { NoticeFilters } from "../utils";

import css from "./SharedFilterGroups.module.scss";

export interface ContractDatesFilterContentProps {
  control: Control<NoticeFilters, unknown>;
  hideNullsWhenSet: (fieldName: "closeDate" | "expiryDate", value: DateFilter) => void;
}

function ContractDatesFilterContent({
  control,
  hideNullsWhenSet,
}: ContractDatesFilterContentProps) {
  const preTenderStages = ["PIPELINE", "PRE_TENDER_V2", "STALE_PRE_TENDER_V2"];
  const tenderStages = ["OPEN_TENDER", "CLOSED_TENDER", "CANCELLED_TENDER", "UNKNOWN_TENDER"];
  const awardedStages = ["AWARDED_CONTRACT", "EXPIRED_CONTRACT", "CANCELLED_AWARD"];
  const selectedProcurementStages = useWatch({ control, name: "stage" });
  const isPreTenderStage = preTenderStages.some((stage) =>
    selectedProcurementStages?.includes(stage as NoticesStagesv2),
  );
  const isTenderStage = tenderStages.some((stage) =>
    selectedProcurementStages?.includes(stage as NoticesStagesv2),
  );
  const isAwardedStage = awardedStages.some((stage) =>
    selectedProcurementStages?.includes(stage as NoticesStagesv2),
  );

  return (
    <div className={css.filterSection} aria-label="Date filters">
      <TooltipComponent
        content={
          isAwardedStage ? (
            <p>
              Unavailable for the selected procurement stages: Awarded contract, Expired contract,
              Cancelled award
            </p>
          ) : null
        }
        side="bottom"
        variant="dark"
      >
        <div>
          <DateRange
            name="closeDate.filter"
            control={control}
            label="Close date"
            onChange={(value) => hideNullsWhenSet("closeDate", value)}
            disabled={isAwardedStage}
          />
          <Checkbox
            label=""
            name="closeDate.hideNulls"
            control={control}
            fieldLabel="Hide notices without close dates"
            disabled={isAwardedStage}
          />
        </div>
      </TooltipComponent>
      <TooltipComponent
        content={
          isTenderStage || isPreTenderStage ? (
            <p>
              Unavailable for the selected procurement stages: Pipeline notice, Pre-tender, Stale
              pre-tender, Open tender, Closed tender, Cancelled tender, Unknown tender
            </p>
          ) : null
        }
        side="bottom"
        variant="dark"
      >
        <div>
          <DateRange
            name="expiryDate.filter"
            control={control}
            label="Expiry date"
            onChange={(value) => hideNullsWhenSet("expiryDate", value)}
            disabled={isTenderStage || isPreTenderStage}
          />
          <Checkbox
            label=""
            name="expiryDate.hideNulls"
            control={control}
            fieldLabel="Hide notices without expiry dates"
            disabled={isTenderStage || isPreTenderStage}
          />
        </div>
      </TooltipComponent>
      <DateRange name="publishDate" control={control} label="Published date" />
    </div>
  );
}

export default ContractDatesFilterContent;
